import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  AlertCircle,
  Copy,
  CheckCircle,
  Lightbulb,
  Target,
  TrendingUp,
  ShoppingBag,
  ArrowRight,
  Zap
} from 'lucide-react';
import { streamOpenAIResponse } from '../../lib/openai';

interface PostIdea {
  idea: string;
  hooks: string[];
}

const PostIdeaBrainstormer = () => {
  const [goal, setGoal] = useState<'Traffic' | 'Engagement' | 'Promo'>('Engagement');
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ideas, setIdeas] = useState<PostIdea[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!topic.trim()) {
      setError('Please enter a topic for your post ideas');
      return;
    }

    setLoading(true);
    setError(null);
    setIdeas([]);

    try {
      const prompt = `You are a senior social-media strategist; given Goal=${goal} ("Traffic","Engagement","Promo"), and Topic=${topic}, reply ONLY with a valid JSON array ideas where each element contains "idea" (catchy title) and "hooks" (array of exactly 3 one-sentence angles).`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            // Clean the response text to handle markdown code blocks
            let cleanedResponse = responseText;
            if (cleanedResponse.includes('```json')) {
              cleanedResponse = cleanedResponse.replace(/```json\n|\n```/g, '');
            } else if (cleanedResponse.includes('```')) {
              cleanedResponse = cleanedResponse.replace(/```\n|\n```/g, '');
            }
            
            const parsedData = JSON.parse(cleanedResponse);
            if (Array.isArray(parsedData)) {
              setIdeas(parsedData);
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          throw error;
        }
      );
    } catch (err) {
      setError('Failed to generate post ideas. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (index: number) => {
    const idea = ideas[index];
    if (!idea) return;
    
    const text = `Post Idea: ${idea.idea}\n\nHooks:\n${idea.hooks.map((hook, i) => `${i+1}. ${hook}`).join('\n')}`;
    
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    
    setTimeout(() => {
      setCopiedIndex(null);
    }, 2000);
  };

  const getGoalIcon = (goalType: string) => {
    switch (goalType) {
      case 'Traffic':
        return <Target className="h-5 w-5 text-blue-500" />;
      case 'Engagement':
        return <TrendingUp className="h-5 w-5 text-purple-500" />;
      case 'Promo':
        return <ShoppingBag className="h-5 w-5 text-green-500" />;
      default:
        return <Lightbulb className="h-5 w-5 text-amber-500" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center space-x-4">
        <Link to="/ai-tools" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Post Idea Brainstormer</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Generate creative post ideas with engaging hooks for your social media content
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Generate Post Ideas</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              What's your goal?
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(['Traffic', 'Engagement', 'Promo'] as const).map((goalType) => (
                <button
                  key={goalType}
                  type="button"
                  onClick={() => setGoal(goalType)}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    goal === goalType
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${
                      goal === goalType
                        ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                        : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      {getGoalIcon(goalType)}
                    </div>
                    <span className="ml-3 font-medium text-gray-900 dark:text-white">{goalType}</span>
                  </div>
                  {goal === goalType && (
                    <CheckCircle className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Topic Input */}
          <div>
            <label htmlFor="topic" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Topic or Niche
            </label>
            <input
              type="text"
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., Digital Marketing, Fitness Tips, Productivity Hacks"
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            />
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading || !topic.trim()}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                loading || !topic.trim()
                  ? 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Lightbulb className="h-5 w-5" />
                  <span>Generate Ideas</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>

      {/* Results */}
      {ideas.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <Lightbulb className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Post Ideas for {goal} ({ideas.length})
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ideas.map((idea, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {idea.idea}
                  </h3>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleCopy(index)}
                    className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                  >
                    {copiedIndex === index ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </motion.button>
                </div>

                <div className="space-y-3 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                    <Zap className="h-4 w-4 mr-1.5 text-indigo-600 dark:text-indigo-400" />
                    Hooks
                  </h4>
                  <ul className="space-y-2">
                    {idea.hooks.map((hook, hookIndex) => (
                      <li key={hookIndex} className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-medium mr-2 mt-0.5">
                          {hookIndex + 1}
                        </span>
                        <p className="text-gray-700 dark:text-gray-300">{hook}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-4">
          <Lightbulb className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Pro Tips</h3>
        </div>
        <ul className="space-y-3 text-indigo-100">
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Choose <strong>Traffic</strong> when you want to drive visitors to your website or landing page</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Select <strong>Engagement</strong> to boost likes, comments, and shares on your posts</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Pick <strong>Promo</strong> when promoting products, services, or special offers</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Be specific with your topic for more targeted and effective post ideas</span>
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

export default PostIdeaBrainstormer;